// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import { boot, getMeta } from './data';
import { currentRoute, navigate, onRouteChange, DEFAULT_VIEW } from './router';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossary';
import { closeAllOverlays } from './overlay';
import { openAbout } from './about';
import { esc, num, longDate } from './format';
import { SCOPE_SHORT } from './theme';

import { renderStart } from './views/start';
import { renderMap } from './views/map';
import { renderJurisdictions } from './views/jurisdictions';
import { renderTimeline } from './views/timeline';
import { renderPending } from './views/pending';
import { renderAgreements } from './views/agreements';
import { renderHolders } from './views/holders';
import { renderRegister } from './views/register';

/**
 * Nav labels are WORDS ONLY — never a count badge — and they are written to
 * read as a progression rather than as a list of dataset names: start here,
 * see where it applies, understand why it is uneven, watch it happen, see what
 * is unfinished, see what was negotiated after, see who holds the result, and
 * read the record itself.
 */
const VIEWS: { id: string; label: string; render: (host: HTMLElement) => void | Promise<void>; narrow?: boolean }[] = [
  { id: 'start', label: 'Start here', render: renderStart, narrow: true },
  { id: 'map', label: 'Where it applies', render: renderMap },
  { id: 'jurisdictions', label: 'State by state', render: renderJurisdictions },
  { id: 'timeline', label: 'Since 1992', render: renderTimeline },
  { id: 'pending', label: 'Still open', render: renderPending },
  { id: 'agreements', label: 'What was agreed', render: renderAgreements },
  { id: 'holders', label: 'Who holds it', render: renderHolders },
  { id: 'register', label: 'The record', render: renderRegister },
];

function shell(): HTMLElement {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <a class="skip-link" href="#main-content">Skip to content</a>
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#/${DEFAULT_VIEW}">
          <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="12" fill="#7a4f18"/>
            <path d="M8 44c8-3 14-4 24-4s16 1 24 4v10H8z" fill="#c98f45"/>
            <path d="M8 44c8-3 14-4 24-4s16 1 24 4" fill="none" stroke="#f2dcbb" stroke-width="2.4"/>
            <circle cx="32" cy="24" r="11" fill="none" stroke="#f2dcbb" stroke-width="3.2"/>
            <circle cx="32" cy="24" r="4" fill="#f2dcbb"/>
          </svg>
          <span class="brand-name">Native Title</span>
        </a>
        <div class="header-spacer"></div>
        <button type="button" class="icon-btn" id="about-btn" aria-label="About this site, its sources and its limitations">About &amp; sources</button>
      </div>
    </header>
    <nav class="site-nav" aria-label="Views">
      <div class="nav-inner" id="nav-inner"></div>
    </nav>
    <main class="main-content" id="main-content" tabindex="-1"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-sources" id="footer-sources"></div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>
  `;

  const nav = app.querySelector<HTMLElement>('#nav-inner')!;
  for (const view of VIEWS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'nav-tab';
    b.textContent = view.label;
    b.dataset.view = view.id;
    b.addEventListener('click', () => navigate(view.id));
    nav.append(b);
  }

  app.querySelector<HTMLButtonElement>('#about-btn')!.addEventListener('click', () => openAbout());

  // The skip link must not reach the router. `href="#main-content"` sets
  // location.hash, which fires hashchange, which the router would read as the
  // view "main-content" — an unknown view, so it would fall back to the default
  // and throw the reader out of whatever they were reading.
  app.querySelector<HTMLAnchorElement>('.skip-link')!.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('main-content')?.focus();
  });

  return app.querySelector<HTMLElement>('#main-content')!;
}

function markActive(id: string): void {
  for (const tab of document.querySelectorAll<HTMLElement>('.nav-tab')) {
    const on = tab.dataset.view === id;
    if (on) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    if (on) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

let renderToken = 0;

async function route(main: HTMLElement): Promise<void> {
  // An overlay opened on one view must not survive into the next: on a phone a
  // drawer is full-width, so the reader would appear to be browsing behind it.
  closeAllOverlays();

  const { view } = currentRoute();
  const entry = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
  markActive(entry.id);

  // A view render is async. Without a token, navigating twice quickly lets a
  // slow first render append its DOM into a host the second render already
  // replaced, and the reader ends up with two views stacked.
  const token = ++renderToken;
  main.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = `view-wrap${entry.narrow ? ' narrow' : ''}`;
  wrap.innerHTML = '<div class="loading">Loading…</div>';
  main.append(wrap);
  try {
    const host = document.createElement('div');
    await entry.render(host);
    if (token !== renderToken) return;
    wrap.replaceChildren(...host.childNodes);
  } catch (err) {
    if (token !== renderToken) return;
    wrap.innerHTML = `<div class="error-state"><strong>This view could not be shown.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p>
      <p class="small">The registers themselves are published by the National Native Title Tribunal at
        <a href="https://data.gov.au/data/organization/national-native-title-tribunal" target="_blank" rel="noopener">data.gov.au</a>.</p></div>`;
  }
}

async function start(): Promise<void> {
  const main = shell();
  main.innerHTML = '<div class="view-wrap"><div class="loading">Loading the native title registers…</div></div>';

  try {
    await boot();
  } catch (err) {
    main.innerHTML = `<div class="view-wrap"><div class="error-state">
      <strong>The registers could not be loaded.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p>
      <p class="small">They are published by the National Native Title Tribunal at
        <a href="https://data.gov.au/data/organization/national-native-title-tribunal" target="_blank" rel="noopener">data.gov.au</a>.</p>
      <button type="button" class="btn" id="retry">Try again</button></div></div>`;
    document.getElementById('retry')?.addEventListener('click', () => location.reload());
    return;
  }

  const meta = getMeta();
  const extracted = meta.sources.map((s) => s.extracted).filter(Boolean).sort();
  document.querySelector('#footer-sources')!.innerHTML = `
    Source: the National Native Title Tribunal’s registers, published via
    <a href="https://data.gov.au/data/organization/national-native-title-tribunal" target="_blank" rel="noopener">data.gov.au</a>
    under <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a> —
    ${esc(num(meta.counts.determinations))} determinations, ${esc(num(meta.counts.agreements))} agreements and
    ${esc(num(meta.counts.registeredClaims + meta.counts.scheduledApplications))} live matters,
    extracted ${esc(longDate(extracted[extracted.length - 1] ?? null))}.
    <br><span class="xsmall">${esc(SCOPE_SHORT)}. An independent project; not affiliated with the Tribunal.</span>`;

  initTooltip();
  initGlossary();
  if (!location.hash) history.replaceState(null, '', `#/${DEFAULT_VIEW}`);
  await route(main);
  onRouteChange(() => { void route(main); });
}

void start();
